import React, { useContext, useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { Typography, makeStyles } from '@material-ui/core';
import { WidgetPrintHeader } from '@mummssoftware/common-ui';
import {
  formatMMDDYYhh,
  getCreatedByString,
  getPhysicianString,
  getPsoRoles,
  getRoles,
  getRolesString,
} from '@mummssoftware/utils/utils';
import { PNGlobalContext } from '@mummssoftware/global-context';
import {
  PSOContent,
  PSOSignaturePrintComponent,
  SignatureContainer,
  StandingOrder,
  WidgetPrintHeaderWrapper,
} from '../../components/layout/WrapperDiv';
import { formatMMDDYY } from '../../utils/utils';
import { ProgressNotesContext } from '../Context/ProgressNotesContext';
import defaults from '../../translations/en.json';
import { getPsoWithSameEntityKey } from '../ProgressNotes/utils/utils';
import { NoteDiv, NoteWrapperDiv } from './StyledDiv';

type PSOSignaturePrintProps = {
  note: mumms.progressNotebyEntity | mumms.progressNote;
  standingOrder?: any;
};

type StandingOrderDetailsProps = {
  pso: any;
};

const useStyles = makeStyles({
  root: {
    fontWeight: 600,
  },
});

export const PSOSignaturePrint = ({
  note,
  standingOrder,
}: PSOSignaturePrintProps) => {
  let standingOrderUI: any | null;
  let allPSOData: any | null;

  const classes = useStyles();

  const { ordersGlobalMapEffectiveDate } = useContext(ProgressNotesContext);
  const { patientOrdersGlobal } = useContext(PNGlobalContext);

  const [verbalSignature, setVerbalSignature] = useState<string>();
  const [physicalSignature, setPhysicalSignature] = useState<string>();

  const [displayPSOContent, setDisplayPSOContent] = useState<boolean>(false);

  const handleAccordionClick = () => {
    if (!displayPSOContent) {
      setDisplayPSOContent(true);
    } else {
      setDisplayPSOContent(false);
    }
  };

  const noteInfoId =
    (standingOrder?.info[0] && Number(standingOrder.info[0].entitykey)) ||
    (standingOrder?.info && Number(standingOrder.info.entitykey));

  const StandingOrderDetails = ({ pso }: StandingOrderDetailsProps) => {
    /* standingOrder.info = present if there is a standing order associated */
    if (pso.info) {
      /* if there is a standing order for the note, take out its id (entitykey) and reference its location
      in ordersGlobalMapEffective which contains the rest of the PSO data/UI */

      const psoWithSameEntityKey = getPsoWithSameEntityKey(
        ordersGlobalMapEffectiveDate,
        noteInfoId,
      );
      const standingOrderHTMLString =
        noteInfoId && ordersGlobalMapEffectiveDate && psoWithSameEntityKey
          ? psoWithSameEntityKey[noteInfoId].templatebasedtext
          : defaults.noRecords;

      standingOrderUI = standingOrderHTMLString;
      allPSOData = psoWithSameEntityKey && psoWithSameEntityKey[noteInfoId];
    }

    const getStandingOrderDetailsUI = () => {
      if (standingOrderUI && allPSOData) {
        const checkedPrns = getPsoRoles(allPSOData);
        const roles = getRoles(checkedPrns);
        const textRoles = roles.length ? roles.toString() : false;

        const globalOrderSameId = patientOrdersGlobal?.find(
          (globalPSO: any) =>
            globalPSO.patientstandingorderid === allPSOData.id,
        );

        const getPrnDetails = () => (
          <>
            <p>{getCreatedByString(allPSOData)}</p>
            {globalOrderSameId && (
              <p>{getPhysicianString(globalOrderSameId)}</p>
            )}
            {textRoles && <p>{getRolesString(roles, textRoles)}</p>}
          </>
        );

        return (
          <>
            <StandingOrder>
              {getPrnDetails()}
              {parse(standingOrderUI)}
            </StandingOrder>
          </>
        );
      }
      return <></>;
    };

    return getStandingOrderDetailsUI();
  };

  useEffect(() => {
    if (note.note) {
      /* beginningOfString = index of first character of string */
      const beginningOfString = 0;

      /* pulling out all the text AFTER and INCLUDING "Electronically signed by", 
        or "Refer to medical records" */

      const indexOfElectronicSignatureString = note.note.indexOf(
        defaults.electronicallySignedBy,
      );

      const indexOfReferToMedicalRecordsString = note.note.indexOf(
        defaults.referToMedicalRecords,
      );

      /* pulling out all the text BEFORE "Order read back and verified" 
      and plugging it into verbal signature state */

      if (indexOfElectronicSignatureString !== -1) {
        setVerbalSignature(
          note.note.substring(
            beginningOfString,
            indexOfElectronicSignatureString,
          ),
        );
      } else if (indexOfReferToMedicalRecordsString !== -1) {
        setVerbalSignature(
          note.note.substring(
            beginningOfString,
            indexOfReferToMedicalRecordsString,
          ),
        );
      } else {
        setVerbalSignature(note.note);
      }

      if (indexOfElectronicSignatureString !== -1) {
        setPhysicalSignature(
          `${note.note.substring(indexOfElectronicSignatureString)}`,
        );
      } else if (indexOfReferToMedicalRecordsString !== -1)
        setPhysicalSignature(
          `${note.note.substring(indexOfReferToMedicalRecordsString)}`,
        );
    }
  }, [note]);

  return (
    <>
      <PSOSignaturePrintComponent id="pso-and-signature-print-component">
        <PSOContent displayPSOContent={displayPSOContent}>
          {standingOrder && <StandingOrderDetails pso={standingOrder} />}
          {note && verbalSignature && standingOrder && (
            <SignatureContainer>
              <p>{defaults.verbalSignature}: </p>
              <p>{verbalSignature}</p>
            </SignatureContainer>
          )}
          {physicalSignature && note && standingOrder && (
            <SignatureContainer>
              <p>{defaults.physicalSignature}: </p>
              <p>{physicalSignature}</p>
            </SignatureContainer>
          )}
        </PSOContent>
      </PSOSignaturePrintComponent>
      <NoteWrapperDiv displayPSOContent={displayPSOContent}>
        <Typography variant="body1" classes={{ root: classes.root }}>
          {formatMMDDYYhh(note)} - {note.personname} {note.credentials}
        </Typography>
        <NoteDiv>{parse(note.note)}</NoteDiv>
      </NoteWrapperDiv>
    </>
  );
};
